import { useState, useEffect, useCallback } from "react";
import { Row, Col, Table, Avatar, Tag, Input, Select } from "antd";
import { fetchMembers } from "services/memberships.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import { format } from "date-fns";
import {
  RENEWAL_STATUS,
  RENEWAL_STATUS_COLOR,
  MEMBERSHIP_STATUS,
  MEMBERSHIP_STATUS_COLOR,
} from "commons/constants";
import { getPascalCase } from "helpers/common.helper";

const MEMBERSHIP_OPTIONS = [
  { value: "", label: "All Membership Statuses" },
  { value: "pending",   label: "Pending" },
  { value: "active",    label: "Active" },
  { value: "expired",   label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
  { value: "failed",    label: "Failed" },
];

const RENEWAL_OPTIONS = [
  { value: "", label: "All Renewal Statuses" },
  { value: "on_schedule", label: "On Schedule" },
  { value: "retrying", label: "Retrying" },
  { value: "grace_period", label: "Grace Period" },
  { value: "cancelled", label: "Cancelled" },
  { value: "failed", label: "Failed" },
];

const BILLING_OPTIONS = [
  { value: "", label: "All Billing Types" },
  { value: "FREE", label: "Free" },
  { value: "SUBSCRIPTION", label: "Subscription" },
  { value: "ONE_TIME", label: "One-time" },
];

const columns = [
  {
    title: "Member",
    key: "member",
    render: (_, r) => (
      <Row gutter={[8, 0]} align="middle" wrap={false}>
        <Col>
          <Avatar src={r.profile_image_url} size={32}>
            {r.full_name?.[0]?.toUpperCase()}
          </Avatar>
        </Col>
        <Col>
          <p className="font-medium text-sm">{r.full_name}</p>
          <p className="text-xs text-muted-foreground">{r.phone_number}</p>
        </Col>
      </Row>
    ),
  },
  {
    title: "Tier / Plan",
    key: "plan",
    render: (_, r) => (
      <div>
        <p className="text-sm font-medium">{r.tier_name}</p>
        <p className="text-xs text-muted-foreground">{r.plan_name}</p>
      </div>
    ),
  },
  {
    title: "Billing",
    dataIndex: "billing_type",
    render: (v) => <Tag>{v}</Tag>,
  },
  {
    title: "Membership",
    key: "membership_status",
    render: (_, r) => (
      <Tag color={MEMBERSHIP_STATUS_COLOR[MEMBERSHIP_STATUS[r.membership_status]] ?? "default"}>
        {MEMBERSHIP_STATUS[r.membership_status] ?? r.membership_status ?? "—"}
      </Tag>
    ),
  },
  {
    title: "Renewal",
    key: "renewal_status",
    render: (_, r) =>
      r.renewal_status ? (
        <Tag color={RENEWAL_STATUS_COLOR[RENEWAL_STATUS[r.renewal_status]] ?? "default"}>
          {getPascalCase(RENEWAL_STATUS[r.renewal_status])}
        </Tag>
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      ),
  },
  {
    title: "Start Date",
    dataIndex: "membership_start_date",
    render: (v) => (v ? format(new Date(v), "MMM d, yyyy") : "—"),
  },
  {
    title: "End Date",
    dataIndex: "membership_end_date",
    render: (v) => (v ? format(new Date(v), "MMM d, yyyy") : "—"),
  },
  {
    title: "Next Billing",
    dataIndex: "next_billing_date",
    render: (v) => (v ? format(new Date(v), "MMM d, yyyy") : "—"),
  },
];

function MembersTab({ communityId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [membershipFilter, setMembershipFilter] = useState("");
  const [renewalFilter, setRenewalFilter] = useState("");
  const [billingFilter, setBillingFilter] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadMembers = useCallback(
    async (page, pageSize, renewal, billing, membership) => {
      setLoading(true);
      const filters = {};
      if (renewal)    filters.renewal_status    = renewal;
      if (billing)    filters.billing_type      = billing;
      if (membership) filters.membership_status = membership;
      const { data, errors } = await fetchMembers(communityId, {
        current_page: page,
        page_size: pageSize,
        ...filters,
      });
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      setMembers(data?.data ?? []);
      setPagination((prev) => ({
        ...prev,
        total: data?.meta?.pagination?.total ?? 0,
        current: page,
        pageSize,
      }));
    },
    [communityId]
  );

  useEffect(() => {
    loadMembers(1, pagination.pageSize, renewalFilter, billingFilter, membershipFilter);
  }, [renewalFilter, billingFilter, membershipFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (paginationConfig) => {
    loadMembers(
      paginationConfig.current,
      paginationConfig.pageSize,
      renewalFilter,
      billingFilter,
      membershipFilter
    );
  };

  const filtered = search
    ? members.filter(
        (m) =>
          m.full_name?.toLowerCase().includes(search.toLowerCase()) ||
          m.phone_number?.includes(search) ||
          m.tier_name?.toLowerCase().includes(search.toLowerCase())
      )
    : members;

  return (
    <div className="space-y-4">
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} sm={24} md={8}>
          <Input.Search
            placeholder="Search this page by name, phone or tier…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={setSearch}
            allowClear
          />
        </Col>
        <Col xs={24} sm={8} md={5}>
          <Select
            value={membershipFilter}
            onChange={(v) => setMembershipFilter(v)}
            options={MEMBERSHIP_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Select
            value={renewalFilter}
            onChange={(v) => setRenewalFilter(v)}
            options={RENEWAL_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Select
            value={billingFilter}
            onChange={(v) => setBillingFilter(v)}
            options={BILLING_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
        <Table
          rowKey="membership_id"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 800 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            position: ["bottomRight"],
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}

export default MembersTab;
